import React, { useEffect, useRef, useState, useCallback } from 'react';
import L, { LatLngTuple } from 'leaflet';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { IFertilizerMarchResponse } from '../api/types';
import { ResponseDataGet } from '../../../../models/responseDataGet';
import { EntTerritorialsUnits } from '../../../../models/handBooks/territorialsUnits';
import { Input } from 'antd';
import LeasingSelectYear from '../../../mechanization/leasing/Leasingselect';
import styles from './styles_map.module.css';

interface FeedingMapProps {
  latitude: number;
  longitude: number;
  fertilizercropsData?: IFertilizerMarchResponse;
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  selectedYear: number | null;
  onYearChange: (year: number | null) => void;
  onRegionClick: (regionId: number, regionName: string) => void;
  onResetRegion: () => void;
  selectedRegionName: string;
  isLoading: boolean;
  initialRegionId?: number;
}

const MapComponent: React.FC<{ 
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  onRegionClick: (regionId: number, regionName: string) => void;
  initialRegionId?: number;
}> = ({ polygonsData, onRegionClick, initialRegionId }) => {
  const map = useMap();
  const polygonLayersRef = useRef<{[key: number]: L.Polygon}>({});
  const activePolygonRef = useRef<L.Polygon | null>(null);

  const setActivePolygonStyle = useCallback((polygonLayer: L.Polygon) => {
    if (activePolygonRef.current !== polygonLayer) {
      // Сброс стилей всех полигонов
      Object.values(polygonLayersRef.current).forEach(layer => {
        layer.setStyle({
          fillOpacity: 0.1,
          weight: 1,
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)'
        });
      });

      // Установка стилей активного полигона
      polygonLayer.setStyle({
        fillOpacity: 0.3,
        weight: 2,
        color: 'green',
        fillColor: 'green'
      });
      activePolygonRef.current = polygonLayer;
    }
  }, []);

  useEffect(() => {
    if (!map || !polygonsData?.res) return;

    const polygonsArray = Array.isArray(polygonsData.res) ? polygonsData.res : [polygonsData.res];

    Object.values(polygonLayersRef.current).forEach(layer => layer.remove());
    polygonLayersRef.current = {};

    polygonsArray.forEach((polygon: EntTerritorialsUnits) => {
      if (!polygon.id || !polygon.nameRu || !polygon.polygonJson) return;

      try {
        const parsedPolygon = JSON.parse(polygon.polygonJson);
        if (!parsedPolygon || !Array.isArray(parsedPolygon) || !parsedPolygon[0]) return;

        const geoJson = parsedPolygon[0].geojson;
        if (!geoJson || !geoJson.coordinates || !Array.isArray(geoJson.coordinates)) return;

        const coordinates = geoJson.type === 'MultiPolygon' 
          ? geoJson.coordinates[0][0]  
          : geoJson.coordinates[0];    

        if (!Array.isArray(coordinates)) return;

        const latLngs: [number, number][] = coordinates.map((coord: number[]) => [coord[1], coord[0]]);

        const polygonLayer = L.polygon(latLngs, {
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)',
          fillOpacity: 0.1,
          weight: 1
        }).addTo(map);

        polygonLayer.on('click', () => {
          if (polygon.id) {
            setActivePolygonStyle(polygonLayer);
            onRegionClick(polygon.id, polygon.nameRu!);
          }
        });

        if (polygon.id != null) {
          polygonLayersRef.current[polygon.id] = polygonLayer;
          
          // Установка начального активного полигона
          if (initialRegionId && polygon.id === initialRegionId) {
            setActivePolygonStyle(polygonLayer);
          }
        }
      } catch (error) {
        console.error('Error parsing polygon:', error);
      }
    });

    if (Object.values(polygonLayersRef.current).length > 0) {
      const group = L.featureGroup(Object.values(polygonLayersRef.current));
      map.fitBounds(group.getBounds());
    }
  }, [map, polygonsData, onRegionClick, setActivePolygonStyle, initialRegionId]);

  return null;
};

export const FeedingMap: React.FC<FeedingMapProps> = ({
  latitude,
  longitude,
  fertilizercropsData,
  polygonsData,
  selectedYear,
  onYearChange,
  onRegionClick,
  onResetRegion,
  selectedRegionName,
  isLoading,
  initialRegionId
}) => {
  const center: LatLngTuple = [latitude, longitude];
  const [searchText, setSearchText] = useState('');

  const renderCropStats = () => {
    if (isLoading) {
      return <div className={styles.loading}>Загрузка...</div>;
    }

    if (!fertilizercropsData?.res || !Array.isArray(fertilizercropsData.res)) {
      return null;
    }

    const filteredData = fertilizercropsData.res.filter(item =>
      item.cropNameRu.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
      <>
        <div className={styles.searchContainer}>
        <div className={styles.headerContainer}>
            <h3 className={styles.headerTitle}>
              {selectedRegionName ? `${selectedRegionName}` : 'Данные по республике'}
            </h3>
            {selectedRegionName && (
              <button 
                className={styles.resetButton}
                onClick={onResetRegion}
                title="Показать данные по всей республике"
              >
                ×
              </button>
            )}
          </div>
          <Input
            placeholder="Поиск культуры"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <div className={styles.statsInfo}>
          {filteredData.map((item, index) => (
            <div key={index} className={styles.statsSection}>
              <div className={styles.cropInfo}>
                <span className={styles.cropName}>{item.cropNameRu}</span>
                <span className={styles.percentage}>{item.percentage}%</span>
              </div>
              <div className={styles.areaInfo}>
                <span className={styles.areaLabel}>Площадь культуры:</span>
                <span className={styles.areaValue}>{item.cropArea} га</span>
              </div>
              <div className={styles.areaInfo}>
                <span className={styles.areaLabel}>Площадь подкормки:</span>
                <span className={styles.areaValue}>{item.fertilizerArea} га</span>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.mapContainer}>
        <MapContainer
              center={[latitude, longitude]}
              zoom={6}
              minZoom={6}
              maxZoom={6}
              dragging={false}
              scrollWheelZoom={false}
              doubleClickZoom={false}
              touchZoom={false}
              style={{ height: '100%', width: '100%' }}
              attributionControl={false}
              zoomControl={false}
        >
      <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
          <MapComponent
            polygonsData={polygonsData}
            onRegionClick={onRegionClick}
            initialRegionId={initialRegionId}
          />
        </MapContainer>
        <div className={styles.yearSelector}>
          <LeasingSelectYear
            value={selectedYear}
            onChange={onYearChange}
          />
        </div>
      </div>
      <div className={styles.sidebar}>
        <div className={styles.statsContainer}>
          {renderCropStats()}
        </div>
      </div>
    </div>
  );
};
